import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CloseIcon } from '../../styles';
import {
  Content,
  ContentWrapper,
  ErrorMessage,
  Field,
  Header,
  Label,
  Media,
  StyledInput,
  SubmitButton,
  SubTitle,
  SuccessMessage,
  SwitchModeLink,
  Title
} from './styles';

import { resetUserPassword } from '../../../firebase/user';
import { close, getIsFetching, setIsFetching, setShowSignIn, setShowSignUp } from '../../../store/user';
import { useHomePageTag } from '../../../tag-manager/homepage';

const ForgotModal = () => {
  const dispatch = useDispatch();
  const onCloseClick = () => dispatch(close());
  const onSignUpClick = () => dispatch(setShowSignUp());
  const onSignInClick = () => dispatch(setShowSignIn());

  const isFetching = useSelector(getIsFetching);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  // The address we actually sent to, which doubles as the "we are done" flag.
  // Set only after Firebase accepts the request.
  const [sentTo, setSentTo] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const address = email.trim();

    if (address === '') {
      setError('Please enter your email address.');
      return;
    }

    setError('');
    dispatch(setIsFetching(true));
    resetUserPassword(
      address,
      () => {
        useHomePageTag({ label: 'reset password success' });

        dispatch(setIsFetching(false));
        // Previously this closed the modal, which left people with no sign at
        // all that the email had been sent. Show the confirmation instead.
        setSentTo(address);
      },
      (error) => {
        dispatch(setIsFetching(false));
        setError(error);
      }
    );
  };

  if (sentTo) {
    return (
      <ContentWrapper>
        <Media />
        <Content>
          <Header>
            <div>
              <Title>Check your email</Title>
              <SubTitle>We have sent you a link to reset your password.</SubTitle>
            </div>
            <button onClick={onCloseClick}>
              <CloseIcon />
            </button>
          </Header>
          <SuccessMessage>
            <p>
              If an account uses <strong>{sentTo}</strong>, a reset link is on its way. Open the link and choose a new password.
            </p>
            <p>
              The email comes from <strong>support@yedding.com</strong>. If you do not see it in a few minutes, look in your spam or promotions
              folder.
            </p>
            <p>
              The link stops working after one hour. If you asked more than once, only the newest email works — the older links stop working.
            </p>
          </SuccessMessage>
          <SubmitButton type="button" onClick={onSignInClick} value="Back to Sign In" />
          <SwitchModeLink>
            Did not get the email?
            <button
              onClick={() => {
                setSentTo('');
                setError('');
              }}
            >
              Try again
            </button>
          </SwitchModeLink>
        </Content>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <Media />
      <Content>
        <Header>
          <div>
            <Title>Forgot Password?</Title>
            <SubTitle>Please enter your email to recover your account:</SubTitle>
          </div>
          <button onClick={onCloseClick}>
            <CloseIcon />
          </button>
        </Header>
        <form onSubmit={handleSubmit}>
          <Field>
            <Label>Email</Label>
            <StyledInput type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="example@gmail.com" />
          </Field>
          {!!error && <ErrorMessage>{error}</ErrorMessage>}
          <SubmitButton type="submit" disabled={isFetching} value={isFetching ? 'Sending...' : 'Reset Password'} />
        </form>
        <SwitchModeLink>
          Don&apos;t have an account?
          <button onClick={onSignUpClick}>Sign up</button>
        </SwitchModeLink>
      </Content>
    </ContentWrapper>
  );
};

export default ForgotModal;
